import { emptyDraftLedger, type DraftEntry, type DraftLedger, type DraftOperation, type DraftSource } from "../domain/draft";

/**
 * Tauri-only device-local recovery cache for the in-memory Draft ledger (`domain/draft.ts`).
 *
 * This is explicitly an ephemeral Workbench UI preference / draft cache, not authoritative
 * storage: `plan/product-boundary.md` reserves "Workbench UI preference／ephemeral draft" for
 * Workbench while keeping "model truth" with BIM-Hub. Nothing here stores IFC bytes, BIM-Hub
 * model data, or a receipt; it only lets a user resume the exact same in-memory `DraftLedger`
 * (proposed property edits, still unsent to any writer or authority) after restarting the desktop
 * shell. `state.draft` in `main.ts` remains the single live source of truth while the app is
 * running; this module only mirrors it to a local SQLite file so a restart does not silently
 * discard unsent draft work.
 */

export type NativeDraftPersistenceState = "idle" | "loading" | "saving" | "saved" | "unavailable";

export interface NativeSqlAdapter {
  execute(query: string, bindValues: readonly unknown[]): Promise<unknown>;
  select<T>(query: string, bindValues: readonly unknown[]): Promise<T[]>;
}

interface PendingChangeRow {
  readonly fixture_id: string;
  readonly element_id: string;
  readonly property: string;
  readonly operation: DraftOperation;
  readonly source: DraftSource;
  readonly before_json: string;
  readonly after_json: string;
}

const CREATE_TABLE_SQL = `CREATE TABLE IF NOT EXISTS pending_changes (
  fixture_id TEXT NOT NULL,
  element_id TEXT NOT NULL,
  property TEXT NOT NULL,
  operation TEXT NOT NULL,
  source TEXT NOT NULL,
  before_json TEXT NOT NULL,
  after_json TEXT NOT NULL,
  PRIMARY KEY (fixture_id, element_id, property)
)`;

/** Idempotent; safe to call before every load or persist. */
export async function ensureDraftPersistenceSchema(adapter: NativeSqlAdapter): Promise<void> {
  await adapter.execute(CREATE_TABLE_SQL, []);
}

function rowFromEntry(fixtureId: string, entry: DraftEntry): PendingChangeRow {
  return {
    fixture_id: fixtureId,
    element_id: entry.elementId,
    property: entry.property,
    operation: entry.operation,
    source: entry.source,
    before_json: JSON.stringify(entry.before),
    after_json: JSON.stringify(entry.after)
  };
}

function entryFromRow(row: PendingChangeRow): DraftEntry {
  const before = JSON.parse(row.before_json) as unknown;
  return {
    elementId: row.element_id,
    property: row.property,
    before,
    after: JSON.parse(row.after_json) as unknown,
    operation: row.operation,
    source: row.source,
    outcome: "proposed",
    diff: { path: `/properties/${row.property}`, before, after: JSON.parse(row.after_json) as unknown }
  };
}

/**
 * Rebuilds a `DraftLedger` from persisted rows using exactly the same entry ordering and
 * `validation` defaults `domain/draft.ts` produces for a freshly staged ledger, so a
 * load-then-stage round trip is indistinguishable from a ledger that was never persisted.
 */
function ledgerFromRows(fixtureId: string, rows: readonly PendingChangeRow[]): DraftLedger {
  const entries = [...rows]
    .map(entryFromRow)
    .sort((left, right) => left.elementId.localeCompare(right.elementId) || left.property.localeCompare(right.property));
  const base = emptyDraftLedger(fixtureId);
  return {
    ...base,
    entries,
    validation: { ...base.validation, assessedElementCount: new Set(entries.map((entry) => entry.elementId)).size }
  };
}

/** Returns `null` when nothing was ever persisted for this fixture (caller keeps its in-memory empty ledger). */
export async function loadPersistedDraftLedger(adapter: NativeSqlAdapter, fixtureId: string): Promise<DraftLedger | null> {
  await ensureDraftPersistenceSchema(adapter);
  const rows = await adapter.select<PendingChangeRow>(
    "SELECT fixture_id, element_id, property, operation, source, before_json, after_json FROM pending_changes WHERE fixture_id = ?",
    [fixtureId]
  );
  if (rows.length === 0) return null;
  return ledgerFromRows(fixtureId, rows);
}

/** Full replace of the persisted rows for `ledger.fixtureId` with its current in-memory entries. */
export async function persistDraftLedger(adapter: NativeSqlAdapter, ledger: DraftLedger): Promise<void> {
  await ensureDraftPersistenceSchema(adapter);
  await adapter.execute("DELETE FROM pending_changes WHERE fixture_id = ?", [ledger.fixtureId]);
  for (const entry of ledger.entries) {
    const row = rowFromEntry(ledger.fixtureId, entry);
    await adapter.execute(
      `INSERT INTO pending_changes (fixture_id, element_id, property, operation, source, before_json, after_json)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [row.fixture_id, row.element_id, row.property, row.operation, row.source, row.before_json, row.after_json]
    );
  }
}

export async function clearPersistedDraftLedger(adapter: NativeSqlAdapter, fixtureId: string): Promise<void> {
  await ensureDraftPersistenceSchema(adapter);
  await adapter.execute("DELETE FROM pending_changes WHERE fixture_id = ?", [fixtureId]);
}

/**
 * Serializes concurrent persist requests against one adapter so an in-flight
 * delete-then-reinsert is never interleaved with another one for the same fixture, which could
 * otherwise leave the local file with a partial (neither-old-nor-new) row set.
 */
export function createNativeDraftPersister(adapter: NativeSqlAdapter): {
  load(fixtureId: string): Promise<DraftLedger | null>;
  persist(ledger: DraftLedger): Promise<void>;
  clear(fixtureId: string): Promise<void>;
} {
  let queue: Promise<unknown> = Promise.resolve();
  const enqueue = <T>(task: () => Promise<T>): Promise<T> => {
    const next = queue.then(task, task);
    queue = next.catch(() => undefined);
    return next;
  };
  return {
    load: (fixtureId) => enqueue(() => loadPersistedDraftLedger(adapter, fixtureId)),
    persist: (ledger) => enqueue(() => persistDraftLedger(adapter, ledger)),
    clear: (fixtureId) => enqueue(() => clearPersistedDraftLedger(adapter, fixtureId))
  };
}

let nativePersisterPromise: Promise<ReturnType<typeof createNativeDraftPersister>> | null = null;

async function nativePersister(): Promise<ReturnType<typeof createNativeDraftPersister>> {
  if (nativePersisterPromise === null) {
    nativePersisterPromise = import("@tauri-apps/plugin-sql").then(async (module) => {
      const db = await module.default.load("sqlite:aec-workbench-local-drafts.db");
      return createNativeDraftPersister({
        execute: async (query, bindValues) => {
          await db.execute(query, bindValues as unknown[]);
        },
        select: <T,>(query: string, bindValues: readonly unknown[]) => db.select<T[]>(query, bindValues as unknown[])
      });
    }).catch((error: unknown) => {
      nativePersisterPromise = null;
      throw error;
    });
  }
  return nativePersisterPromise;
}

export async function loadNativeDraftLedger(fixtureId: string): Promise<DraftLedger | null> {
  return (await nativePersister()).load(fixtureId);
}

export async function persistNativeDraftLedger(ledger: DraftLedger): Promise<void> {
  await (await nativePersister()).persist(ledger);
}

export async function clearNativeDraftLedger(fixtureId: string): Promise<void> {
  await (await nativePersister()).clear(fixtureId);
}
